import React, { useState } from "react";
import { AppBar, Toolbar, Box, IconButton, Badge, InputBase, Paper, Menu, MenuItem, Avatar, Typography, Divider } from "@mui/material";
import { Search as SearchIcon, Bell, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { useTheme, ThemeToggle } from "../contexts/ThemeContext";
import { notificationService } from "../api/services/slaService";
import { DRAWER_WIDTH } from "./Sidebar";

const ROLE_LABELS = {
  ORG_ADMIN: "Org Admin",
  MANAGER: "Manager",
  TEAM_LEAD: "Team Lead",
  SUPPORT_ENGINEER: "Support Engineer",
  CLIENT: "Client",
};

export default function Topbar() {
  const navigate = useNavigate();
  const { logout, me } = useAuth();
  const { T } = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: notifications } = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: () => notificationService.list(true),
    refetchInterval: 30000,
  });
  const unreadCount = notifications?.length || 0;

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
  };

  const initials = me?.name ? me.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() : "U";

  return (
    <AppBar position="fixed" sx={{ width: `calc(100% - ${DRAWER_WIDTH}px)`, ml: `${DRAWER_WIDTH}px` }} elevation={0}>
      <Toolbar sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
        <Paper
          component="form"
          onSubmit={handleSearchSubmit}
          variant="outlined"
          sx={{ display: "flex", alignItems: "center", px: 1.5, py: 0.5, width: 360, borderRadius: 2.5, backgroundColor: T.inp_bg }}
        >
          <SearchIcon size={17} color={T.muted} />
          <InputBase
            placeholder="Search tickets & knowledge base..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ ml: 1, flex: 1, fontSize: 14, color: T.text }}
          />
        </Paper>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ThemeToggle />
          <IconButton onClick={() => navigate("/notifications")} sx={{ color: T.text }}>
            <Badge badgeContent={unreadCount} sx={{ "& .MuiBadge-badge": { backgroundColor: T.red, color: "#fff" } }}>
              <Bell size={20} />
            </Badge>
          </IconButton>
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: T.cyan, color: T.scheme === "dark" ? "#06101F" : "#fff", fontSize: 13, fontWeight: 700 }}>
              {initials}
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
            PaperProps={{ sx: { backgroundColor: T.dropdown_bg, border: `1px solid ${T.border}` } }}>
            <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: T.text }}>{me?.name || "..."}</Typography>
              <Typography variant="caption" sx={{ color: T.muted }}>{me?.email}</Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, fontWeight: 600, color: T.cyan }}>
                {ROLE_LABELS[me?.role_code] || me?.role_code}
              </Typography>
            </Box>
            <Divider sx={{ borderColor: T.border }} />
            <MenuItem
              sx={{ color: T.text }}
              onClick={() => {
                setAnchorEl(null);
                logout();
                navigate("/login");
              }}
            >
              <LogOut size={16} style={{ marginRight: 8 }} /> Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}